/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * A FIT message: a bag of numbered fields.
 *
 * This untyped form is the real data model. The generated `*Mesg` subclasses add
 * named accessors over the same field numbers and hold no state of their own, so
 * anything reachable through them is reachable here.
 */
public open class Mesg(
    public val mesgName: String,
    public val globalMesgNum: UShort,
) {
    // Insertion-ordered so that re-encoding preserves the producer's field order.
    // Keyed by Int rather than UByte to keep boxing out of the decode loop.
    internal val fieldMap: LinkedHashMap<Int, Field> = LinkedHashMap()
    internal val developerFieldMap: LinkedHashMap<DeveloperDataKey, DeveloperField> = LinkedHashMap()

    /** Local message number this was read with, or will be written with. */
    public var localMesgNum: UByte = 0u
        internal set

    /** Position of this message in the file, counting from 0. */
    public var decoderMesgIndex: Int = 0
        internal set

    /**
     * Copy constructor. Drops fields that ended up with no values.
     *
     * The fields are deep-copied: a copy and its original share no mutable
     * state, so mutating either one leaves the other untouched.
     */
    public constructor(other: Mesg) : this(other.mesgName, other.globalMesgNum) {
        localMesgNum = other.localMesgNum
        decoderMesgIndex = other.decoderMesgIndex
        for ((num, field) in other.fieldMap) {
            if (field.hasValues) fieldMap[num] = Field(field)
        }
        for ((key, field) in other.developerFieldMap) {
            if (field.hasValues) developerFieldMap[key] = DeveloperField(field)
        }
    }

    /**
     * Adoption constructor: takes over [other]'s fields without copying them.
     *
     * Internal, and only for a caller that owns [other] outright and discards
     * it afterwards — the decoder, which builds a fresh [Mesg] per record and
     * hands it to [Factory.typed]. Sharing the fields there is safe precisely
     * because nothing else ever saw them; anywhere else, use the copy
     * constructor. [adopt] only distinguishes this signature from the copy
     * constructor's.
     */
    internal constructor(other: Mesg, @Suppress("UNUSED_PARAMETER") adopt: Boolean) :
        this(other.mesgName, other.globalMesgNum) {
        localMesgNum = other.localMesgNum
        decoderMesgIndex = other.decoderMesgIndex
        fieldMap.putAll(other.fieldMap)
        developerFieldMap.putAll(other.developerFieldMap)
    }

    /**
     * Removes fields left with no values, in place. What the copy constructor
     * does by omission, for the adoption path: a field whose every value was
     * the invalid sentinel stops being "present".
     */
    internal fun dropEmptyFields() {
        fieldMap.entries.removeAll { !it.value.hasValues }
        developerFieldMap.entries.removeAll { !it.value.hasValues }
    }

    public val fieldList: List<Field> get() = fieldMap.values.toList()

    public val developerFieldList: List<DeveloperField> get() = developerFieldMap.values.toList()

    // No fieldCount property here: FIT has a field called `field_count` on
    // exd_screen_configuration, and the generated accessor for it would clash.
    // Use fieldList.size.

    // ---------------------------------------------------------------- fields

    public fun getField(fieldNum: UByte): Field? = fieldMap[fieldNum.toInt()]

    public fun getField(fieldName: String): Field? = fieldMap.values.firstOrNull { it.fieldName == fieldName }

    public fun hasField(fieldNum: UByte): Boolean = fieldMap.containsKey(fieldNum.toInt())

    public fun setField(field: Field) {
        fieldMap[field.fieldNum.toInt()] = field
    }

    public fun removeField(fieldNum: UByte) {
        fieldMap.remove(fieldNum.toInt())
    }

    /** Drops the fields that were synthesised by component expansion. */
    public fun removeExpandedFields() {
        fieldMap.values.filter { it.isExpandedField }.forEach { fieldMap.remove(it.fieldNum.toInt()) }
    }

    // ---------------------------------------------------------------- values

    /**
     * The value of field [fieldNum], scale and offset applied, or null when the
     * field is absent or holds FIT's invalid sentinel.
     */
    public fun getFieldValue(
        fieldNum: UByte,
        index: Int = 0,
        selector: SubFieldSelector = SubFieldSelector.Active,
    ): Any? {
        val field = getField(fieldNum) ?: return null
        val subField = field.resolveSubField(selector, this)
        if (selector is SubFieldSelector.AtIndex || selector is SubFieldSelector.Named) {
            // An explicitly named subfield that this message does not satisfy
            // would give a value under the wrong interpretation.
            if (subField != null && !subField.canMesgSupport(this)) return null
        }
        return field.getValue(index, subField)
    }

    public fun setFieldValue(
        fieldNum: UByte,
        value: Any?,
        index: Int = 0,
        selector: SubFieldSelector = SubFieldSelector.MainField,
    ) {
        val field = getField(fieldNum) ?: createFieldFor(fieldNum, value).also { setField(it) }
        val subField = field.resolveSubField(selector, this)
        if (subField != null && !subField.canMesgSupport(this)) {
            throw FitFieldException(
                "subfield ${subField.name} is not active on $mesgName; set its reference field first",
            )
        }
        field.setValue(value, index, subField)
    }

    /**
     * The index of the subfield [fieldNum] currently resolves to, or -1 when the
     * field's own meaning applies.
     */
    public fun getActiveSubFieldIndex(fieldNum: UByte): Int {
        val field = getField(fieldNum) ?: return MAIN_FIELD
        val index = field.subFields.indexOfFirst { it.canMesgSupport(this) }
        return if (index >= 0) index else MAIN_FIELD
    }

    private fun createFieldFor(fieldNum: UByte, value: Any?): Field {
        Factory.createField(globalMesgNum, fieldNum)?.let { return it }
        val baseType = BaseType.of(value)
            ?: throw FitFieldException("cannot infer a FIT base type for a value of ${value?.let { it::class.simpleName }}")
        return Factory.createDefaultField(fieldNum, baseType)
    }

    // ------------------------------------------------------ developer fields

    public fun getDeveloperField(key: DeveloperDataKey): DeveloperField? = developerFieldMap[key]

    public fun setDeveloperField(field: DeveloperField) {
        developerFieldMap[field.key] = field
    }

    // ------------------------------------------------------------------ read

    /** Reads one data record laid out by [definition]. */
    internal fun read(
        reader: ByteReader,
        definition: MesgDefinition,
        accumulator: Accumulator,
        expandComponents: Boolean = true,
        developerFields: (DeveloperFieldDefinition) -> DeveloperField? = { null },
    ) {
        for (fieldDefinition in definition.fieldDefinitions) {
            val baseType = fieldDefinition.baseType
            if (baseType == null) {
                // Unknown type byte: the width is still trustworthy, so skip
                // exactly that much and keep the record aligned.
                reader.skip(fieldDefinition.size)
                continue
            }

            // Fields are created in the order the definition record lists them,
            // not in profile declaration order, so a decoded message preserves
            // the producer's layout and re-encoding does not reshuffle it.
            val field = getField(fieldDefinition.num)
                ?: Factory.createField(globalMesgNum, fieldDefinition.num)?.also { setField(it) }
                ?: Factory.createDefaultField(fieldDefinition.num, baseType).also { setField(it) }

            field.declaredTypeId = fieldDefinition.typeId
            field.read(reader, fieldDefinition.size, definition.endianness)

            if (field.isAccumulated) seedAccumulator(field, accumulator)
        }

        // Expansion has to run after every field is read: a component's
        // destination field may be one that appears later in the record.
        if (expandComponents) {
            val expanded = HashSet<Int>()
            fieldMap.values.toList().forEach { expandComponents(it, accumulator, expanded) }
        }

        for (devDefinition in definition.developerFieldDefinitions) {
            val devField = developerFields(devDefinition)
            if (devField == null) {
                // The declaring field_description has not been seen. The bytes
                // are unreadable but their width is known.
                reader.skip(devDefinition.size)
                continue
            }
            devField.read(reader, devDefinition.size, definition.endianness)
            setDeveloperField(devField)
        }
    }

    private fun seedAccumulator(field: Field, accumulator: Accumulator) {
        for (index in 0 until field.numValues) {
            var value = FitValues.toDouble(field.getRawValue(index))

            // If some other field packs this one as a component, the seed must
            // be expressed in the component's units, not the field's.
            outer@ for (containing in fieldMap.values) {
                for (component in containing.components) {
                    if (component.fieldNum == field.fieldNum && component.accumulate) {
                        value = ((value / field.scale) - field.offset + component.offset) * component.scale
                        break@outer
                    }
                }
            }

            accumulator.create(globalMesgNum, field.fieldNum, value.toLong())
        }
    }

    /**
     * Unpacks [containingField]'s bit-packed components into their destination
     * fields, recursively.
     *
     * [expandedSoFar] records which destination fields this message has already
     * written, so the first component to reach a field replaces whatever the
     * record stored there directly and later ones append. A record may carry
     * both `avg_speed` and `enhanced_avg_speed`, and the expanded value is the
     * authoritative one.
     */
    private fun expandComponents(
        containingField: Field,
        accumulator: Accumulator,
        expandedSoFar: MutableSet<Int>,
    ) {
        if (!containingField.hasValues) return

        // A field that is entirely the invalid sentinel packs nothing.
        if (containingField.values.none { containingField.baseType.isValid(it) }) return

        // The active subfield decides which set of components applies, so it
        // must be resolved before any bits are read.
        val activeIndex = getActiveSubFieldIndex(containingField.fieldNum)
        val activeSubField = if (activeIndex == MAIN_FIELD) null else containingField.subFields.getOrNull(activeIndex)

        val components = when {
            activeSubField != null && activeSubField.hasComponents -> activeSubField.components
            activeIndex == MAIN_FIELD && containingField.hasComponents -> containingField.components
            else -> return
        }
        if (components.isEmpty()) return

        val bitStream = BitStream(containingField.values.toList(), containingField.baseType)

        for (component in components) {
            if (bitStream.bitsAvailable < component.bits) break

            val destination = fieldMap[component.fieldNum.toInt()]
                ?: Factory.createField(globalMesgNum, component.fieldNum)
                ?: Factory.createDefaultField(component.fieldNum, containingField.baseType)

            val signed = destination.baseType.isSignedInteger
            var raw = bitStream.readBits(component.bits, signed)

            if (component.accumulate) {
                // An all-ones component is the wire's invalid sentinel: it
                // carries no sample, so it must not feed the running total —
                // fed in, the next real sample's delta would be computed
                // against the sentinel rather than the last real sample.
                // fit-python-sdk folds it in before testing invalidity; that
                // corruption is deliberately not reproduced here.
                val mask = if (component.bits >= 64) -1L else (1L shl component.bits) - 1L
                if (raw and mask == mask) {
                    val key = component.fieldNum.toInt()
                    if (expandedSoFar.add(key)) destination.clearValues()
                    destination.isExpandedField = true
                    destination.addRawValue(null)
                    fieldMap[key] = destination
                    continue
                }
                raw = accumulator.accumulate(globalMesgNum, component.fieldNum, raw, component.bits)
            }

            // Undo the component's own scale and offset, then apply the
            // destination field's, so the stored value round-trips.
            val destScale = activeSubField?.scale ?: destination.scale
            val destOffset = activeSubField?.offset ?: destination.offset
            val scaled = ((raw.toDouble() / component.scale) - component.offset + destOffset) * destScale

            val key = component.fieldNum.toInt()
            if (expandedSoFar.add(key)) destination.clearValues()

            destination.isExpandedField = true
            // An all-ones component means the same thing here as on the wire.
            destination.addRawValue(if (scaled == destination.baseType.invalidAsNumber()) null else scaled)
            fieldMap[key] = destination

            if (destination.hasComponents) expandComponents(destination, accumulator, expandedSoFar)
        }
    }

    // ----------------------------------------------------------------- write

    /**
     * Writes one data record laid out by [definition].
     *
     * Every field the definition lists must be present here. A definition record
     * is a promise about the exact byte layout of the records that follow it, so
     * skipping a field the definition declared would not lose that field — it
     * would shift every byte after it, turning the rest of the record, and every
     * record read under the same local message number, into garbage no decoder
     * can detect. Refusing to write is the only safe answer.
     */
    internal fun write(writer: ByteWriter, definition: MesgDefinition) {
        writer.writeByte((definition.localMesgNum.toInt() and Fit.HDR_TYPE_MASK).toUByte())
        for (fieldDefinition in definition.fieldDefinitions) {
            val field = getField(fieldDefinition.num)
                ?: throw FitFieldException(
                    "$mesgName has no field ${fieldDefinition.num}, which the message definition for local " +
                        "message ${definition.localMesgNum} declares as ${fieldDefinition.size} byte(s)",
                )
            field.write(writer, definition.endianness)
        }
        for (devDefinition in definition.developerFieldDefinitions) {
            val devField = developerFieldMap.values
                .firstOrNull { it.fieldNum == devDefinition.num && it.developerDataIndex == devDefinition.developerDataIndex }
                ?: throw FitFieldException(
                    "$mesgName has no developer field ${devDefinition.developerDataIndex}/${devDefinition.num}, " +
                        "which the message definition for local message ${definition.localMesgNum} declares as " +
                        "${devDefinition.size} byte(s)",
                )
            devField.write(writer, definition.endianness)
        }
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is Mesg) return false
        return globalMesgNum == other.globalMesgNum &&
            mesgName == other.mesgName &&
            fieldMap == other.fieldMap &&
            developerFieldMap == other.developerFieldMap
    }

    override fun hashCode(): Int {
        var result = globalMesgNum.toInt()
        result = 31 * result + mesgName.hashCode()
        result = 31 * result + fieldMap.hashCode()
        return result
    }

    override fun toString(): String = "$mesgName(#$globalMesgNum) ${fieldMap.values}"

    public companion object {
        /** [getActiveSubFieldIndex] returns this when no subfield applies. */
        public const val MAIN_FIELD: Int = -1
    }
}
